import { ReactNode, ReactPortal, useEffect, useMemo } from "react";
import { ReactQueryProvider } from "./ReactQuery";
import { AccountProvider } from "./Account";
import { StoreProvider } from "./Store";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { useUTM } from "~/hooks/useUTM";
import { usePartnerStack } from "~/hooks/usePartnerStack";
import { NuqsAdapter } from "nuqs/adapters/react";
import { useLanguageSync } from "~/hooks/useLanguageSync";
import { StatsigProvider } from "@statsig/react-bindings";
import { useAccount } from "~/hooks/useAccount";
import { statsigClientKey } from "~/helpers/environment";

type Props = {
  children: ReactNode | ReactPortal;
};

const STATSIG_ANON_USER_ID_KEY = "alidrop_statsig_anonymous_user_id";

const getAnonymousStatsigUserId = () => {
  if (typeof window === "undefined") {
    return `anon-${Math.random().toString(36).slice(2)}`;
  }

  try {
    const existingUserId = window.localStorage.getItem(
      STATSIG_ANON_USER_ID_KEY
    );
    if (existingUserId) {
      return existingUserId;
    }

    const generatedUserId = `anon-${crypto.randomUUID()}`;
    window.localStorage.setItem(STATSIG_ANON_USER_ID_KEY, generatedUserId);
    return generatedUserId;
  } catch {
    return `anon-${Math.random().toString(36).slice(2)}`;
  }
};

// Language sync component to be used inside the ReactQueryProvider
const LanguageSyncProvider = ({ children }: Props) => {
  useLanguageSync();
  return <>{children}</>;
};

const StatsigAppProvider = ({ children }: Props) => {
  const { account } = useAccount();
  const anonymousStatsigUserId = useMemo(() => getAnonymousStatsigUserId(), []);

  const statsigUser = useMemo(
    () => ({
      userID: account ? String(account.id) : anonymousStatsigUserId,
      email: account?.email,
      custom: {
        signup_origin: account?.signup_origin,
        payment_provider: account?.payment_provider,
      },
    }),
    [account, anonymousStatsigUserId]
  );

  const statsigProviderKey = account
    ? `user:${account.id}`
    : `anon:${anonymousStatsigUserId}`;

  if (!statsigClientKey) {
    return <>{children}</>;
  }

  return (
    <StatsigProvider
      key={statsigProviderKey}
      sdkKey={statsigClientKey}
      user={statsigUser}
    >
      {children}
    </StatsigProvider>
  );
};

export const ContextWrapper = ({ children }: Props) => {
  const { init } = usePartnerStack();

  useUTM();

  useEffect(() => {
    const loadPartnerStack = () => {
      const script = init();
      if (!script) return;
      document.body.appendChild(script);
    };

    // Defer PartnerStack loading to idle time to avoid blocking main thread
    if ("requestIdleCallback" in window) {
      const idleId = requestIdleCallback(loadPartnerStack);
      return () => cancelIdleCallback(idleId);
    } else {
      const timerId = setTimeout(loadPartnerStack, 2500);
      return () => clearTimeout(timerId);
    }
  }, []);

  return (
    <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_OAUTH_ID}>
      <AccountProvider>
        <ReactQueryProvider>
          <StoreProvider>
            <StatsigAppProvider>
              <LanguageSyncProvider>
                <NuqsAdapter>{children}</NuqsAdapter>
              </LanguageSyncProvider>
            </StatsigAppProvider>
          </StoreProvider>
        </ReactQueryProvider>
      </AccountProvider>
    </GoogleOAuthProvider>
  );
};
